import { RichTextField } from '@prismicio/client'
import { Colors } from '@kvika/audur-theme'
import React, { useEffect, useState } from 'react'
import { NumberFormatValues } from 'react-number-format'
import { v4 as uuidv4 } from 'uuid'

import { NumberFormatCurrencyInput } from '../../../components/AudurBaseInput'
import AudurRadioButton from '../../../components/audurRadioButton/audurRadioButton'
import AudurTooltip from '../../../components/audurTooltip/AudurTooltip'

import styles from './calculatorInputWithRadio.module.scss'

type ICaculatorInputWithOptions = {
	label?: string
	onChange: (event: {
		target: {
			name: string
			value: string
		}
	}) => void
	inputRef?(ref: React.RefObject<HTMLInputElement>): void
	name: string
	value?: string | number
	onBlur?: () => void
	error?: boolean
	style?: React.CSSProperties
	helperText?: string
	id?: string
	width: string
	maxLength?: number
	placeholder?: string
	autoFocus?: boolean
	getIsAllowedValue: (values: NumberFormatValues) => boolean
	decimalScale?: number
	suffix?: string
	min?: number
	max?: number
	defaultValue?: number
	tooltip?: string | RichTextField
	hideTooltip?: boolean
	allowNegative?: boolean
	options?: {
		name: string
		checked: boolean
		label: string
		value: string | number
		tooltip?: string
		onChange: () => void
	}[]
}

const CalculatorInputWithOptions = (props: ICaculatorInputWithOptions) => {
	const { options, label, tooltip, hideTooltip, min, max, onChange, value, name, allowNegative, defaultValue } = props
	const [localValue, setLocalValue] = useState(value)

	useEffect(() => {
		setLocalValue(value)
	}, [value])

	const handleChange = (event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } }) => {
		const cleanedValue = event.target.value.replace(/[^0-9.-]+/g, '')

		// If the value is just a minus sign, keep it to allow negative number input
		if (cleanedValue === '-') {
			setLocalValue('-')
			return
		}

		const parsedValue = parseFloat(cleanedValue)
		setLocalValue(parsedValue)
	}

	return (
		<div className={styles.wrap}>
			<div className={styles.title}>
				{label}
				{!hideTooltip && tooltip && (
					<AudurTooltip
						text={tooltip}
						infoOutlinedColor={Colors.LightGray}
						activeInfoOutlinedColor={Colors.Silver}
						backgroundColor={Colors.Silver}
						hideCloseIcon
						hideTitleRow
						id={uuidv4()}
					/>
				)}
			</div>
			<div className={styles.input}>
				<NumberFormatCurrencyInput
					{...props}
					label={undefined}
					onChange={handleChange}
					value={localValue}
					allowNegative={allowNegative}
					onBlur={() => {
						const numericLocalValue = typeof localValue === 'string' ? parseFloat(localValue) : localValue

						if (numericLocalValue && max && numericLocalValue > max) {
							onChange({
								target: {
									name,
									value: max.toString(),
								},
							})
							setLocalValue(max)
						} else if (numericLocalValue && min && numericLocalValue < min) {
							onChange({
								target: {
									name,
									value: min.toString(),
								},
							})
							setLocalValue(min)
						} else {
							const isValueValid = numericLocalValue || numericLocalValue === 0
							const defaultValueString =
								defaultValue || defaultValue === 0 ? defaultValue.toString() : min ? min.toString() : ''

							onChange({
								target: {
									name,
									value: isValueValid ? numericLocalValue.toString() : defaultValueString,
								},
							})
							setLocalValue(isValueValid ? numericLocalValue.toString() : defaultValueString)
						}
					}}
				/>
			</div>
			{options && options.length > 0 && (
				<div className={styles.radioRow}>
					{options.map((option) => (
						<AudurRadioButton key={option.name + option.value} {...option} tooltip={option.tooltip} />
					))}
				</div>
			)}
		</div>
	)
}

export default CalculatorInputWithOptions
